import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type { StatusInterface } from "interface/common";
import type {
  ApiResponse,
  VehicleTableFilterParams,
  VehicleDetailsApiResponse,
} from "interface/vehicle";

const getFormattedParams = (params: VehicleTableFilterParams = {}) => {
  const normalizeFilterValue = (value?: string | string[] | null): string => {
    if (Array.isArray(value)) return value[0] ?? "";
    return value ?? "";
  };
  const normalizeStatusValue = (
    value?: Array<string | boolean> | string | boolean | null
  ): string => {
    if (Array.isArray(value)) return value.map((item) => String(item)).join("-");
    return value !== null && value !== undefined ? String(value) : "";
  };

  const formatted: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.pageSize ?? 10,
  };

  const registrationNumber = normalizeFilterValue(params.registration_number);
  const vehicleName = normalizeFilterValue(params.vehicle_name);
  const fuelType = normalizeStatusValue(params.fuel_type as string[] | null | undefined);
  const rideType = normalizeStatusValue(params.ride_type as string[] | null | undefined);
  const vehicleCategory = normalizeStatusValue(params.vehicle_category as string[] | null | undefined);
  const availableForDriver = normalizeStatusValue(params.available_for_driver as string[] | null | undefined);
  const status = normalizeStatusValue(params.status as string[] | null | undefined);
  const updatedAt = normalizeFilterValue(params.updatedAt);

  if (registrationNumber) formatted.registration_number = registrationNumber;
  if (vehicleName) formatted.vehicle_name = vehicleName;
  if (fuelType) formatted.fuel_type = fuelType;
  if (rideType) formatted.ride_type = rideType;
  if (vehicleCategory) formatted.vehicle_category = vehicleCategory;
  if (availableForDriver) formatted.available_for_driver = availableForDriver;
  if (status) formatted.status = status;
  if (updatedAt) formatted.updatedAt = updatedAt;

  if (params.sortField) {
    formatted.sort = params.sortField;
    if (params.sortOrder) {
      formatted.direction = params.sortOrder === "ascend" ? "asc" : "desc";
    }
  }

  return formatted;
};

export const vehicleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query<ApiResponse, VehicleTableFilterParams>({
      query: (params) => ({
        url: API.GET_VEHICLE,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["Vehicle"],
    }),
    getActiveVehicleList: builder.query<ApiResponse, void>({
      query: () => ({
        url: API.GET_VEHICLE_LIST,
        method: "GET",
      }),
      providesTags: ["Vehicle"],
    }),
    getVehicleDetails: builder.query<
      VehicleDetailsApiResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/${API.GET_VEHICLE}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Vehicle", id }],
    }),
    downloadVehicleReport: builder.mutation<
      ApiResponse,
      VehicleTableFilterParams | void
    >({
      query: (params) => ({
        url: `/${API.GET_VEHICLE}/download`,
        method: "GET",
        params: getFormattedParams(params ?? {}),
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileUrl = (result.data?.data as Record<string, unknown>)?.fileUrl;
          if (typeof fileUrl === "string") {
            window.open(fileUrl, "_blank");
          }
        } catch (error) {
          console.error("Failed to download vehicle report:", error);
        }
      },
    }),
    createVehicle: builder.mutation<ApiResponse, FormData>({
      query: (formData) => ({
        url: API.GET_VEHICLE,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Vehicle"],
    }),
    updateVehicle: builder.mutation<ApiResponse, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/${API.GET_VEHICLE}/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Vehicle", id }, "Vehicle"],
    }),
    updateVehicleStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...body }) => ({
        url: `/${API.GET_VEHICLE}/status/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Vehicle"],
    }),
    deleteVehicle: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_VEHICLE}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Vehicle"],
    }),
    bulkUpdateVehicleStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_VEHICLE}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["Vehicle"],
    }),
    bulkDeleteVehicles: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_VEHICLE}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Vehicle"],
    }),
    removeVehicleDriverAssignment: builder.mutation<ApiResponse, { vehicleId: string; driverId: string }>({
      query: ({ vehicleId, driverId }) => ({
        url: `/${API.GET_VEHICLE}/${vehicleId}/driver-assignment`,
        method: "DELETE",
        body: { driverId },
      }),
      invalidatesTags: ["Vehicle", "Driver"],
    }),
    getDriversForAssignment: builder.query<
      { statusCode: number; data: { results: DriverForAssignment[] } },
      { q?: string }
    >({
      query: ({ q }) => ({
        url: `/${API.GET_VEHICLE}/drivers-for-assignment`,
        method: "GET",
        params: q?.trim() ? { q: q.trim() } : {},
      }),
    }),
    assignVehicleSchedule: builder.mutation<
      ApiResponse,
      {
        vehicleId: string;
        driver_id: string;
        start_at: string;
        end_at: string;
        notes?: string;
      }
    >({
      query: ({ vehicleId, ...body }) => ({
        url: `/${API.GET_VEHICLE}/${vehicleId}/assign-schedule`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Vehicle", "Driver", "VehicleAssignmentHistory"],
    }),
    getVehicleAssignmentHistory: builder.query<
      {
        statusCode: number;
        data: {
          results: VehicleAssignmentHistoryRow[];
          total: number;
          page: number;
          limit: number;
        };
      },
      {
        page?: number;
        limit?: number;
        vehicle_id?: string;
        driver_id?: string | string[];
        start_at_from?: string;
        start_at_to?: string;
        end_at_from?: string;
        end_at_to?: string;
        created_at_from?: string;
        created_at_to?: string;
        fine_min?: number;
        fine_max?: number;
        sort_by?:
          | "createdAt"
          | "driver_name"
          | "fine"
          | "start_at"
          | "end_at";
        sort_order?: "asc" | "desc";
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        };
        if (params.vehicle_id) queryParams.vehicle_id = params.vehicle_id;
        if (params.driver_id) {
          queryParams.driver_id = Array.isArray(params.driver_id)
            ? params.driver_id.join(",")
            : params.driver_id;
        }
        if (params.start_at_from) queryParams.start_at_from = params.start_at_from;
        if (params.start_at_to) queryParams.start_at_to = params.start_at_to;
        if (params.end_at_from) queryParams.end_at_from = params.end_at_from;
        if (params.end_at_to) queryParams.end_at_to = params.end_at_to;
        if (params.created_at_from) queryParams.created_at_from = params.created_at_from;
        if (params.created_at_to) queryParams.created_at_to = params.created_at_to;
        if (params.fine_min !== undefined && params.fine_min !== null) {
          queryParams.fine_min = params.fine_min;
        }
        if (params.fine_max !== undefined && params.fine_max !== null) {
          queryParams.fine_max = params.fine_max;
        }
        if (params.sort_by) queryParams.sort_by = params.sort_by;
        if (params.sort_order) queryParams.sort_order = params.sort_order;
        return {
          url: `/${API.GET_VEHICLE}/assignment-history`,
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ["VehicleAssignmentHistory"],
    }),
    downloadAssignmentHistory: builder.mutation<
      ApiResponse,
      {
        vehicle_id?: string;
        driver_id?: string | string[];
        start_at_from?: string;
        start_at_to?: string;
        end_at_from?: string;
        end_at_to?: string;
        created_at_from?: string;
        created_at_to?: string;
        fine_min?: number;
        fine_max?: number;
        sort_by?:
          | "createdAt"
          | "driver_name"
          | "fine"
          | "start_at"
          | "end_at";
        sort_order?: "asc" | "desc";
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params.vehicle_id) queryParams.vehicle_id = params.vehicle_id;
        if (params.driver_id) {
          queryParams.driver_id = Array.isArray(params.driver_id)
            ? params.driver_id.join(",")
            : params.driver_id;
        }
        if (params.start_at_from) queryParams.start_at_from = params.start_at_from;
        if (params.start_at_to) queryParams.start_at_to = params.start_at_to;
        if (params.end_at_from) queryParams.end_at_from = params.end_at_from;
        if (params.end_at_to) queryParams.end_at_to = params.end_at_to;
        if (params.created_at_from) queryParams.created_at_from = params.created_at_from;
        if (params.created_at_to) queryParams.created_at_to = params.created_at_to;
        if (params.fine_min !== undefined && params.fine_min !== null) {
          queryParams.fine_min = String(params.fine_min);
        }
        if (params.fine_max !== undefined && params.fine_max !== null) {
          queryParams.fine_max = String(params.fine_max);
        }
        if (params.sort_by) queryParams.sort_by = params.sort_by;
        if (params.sort_order) queryParams.sort_order = params.sort_order;
        return {
          url: `/${API.GET_VEHICLE}/assignment-history/download`,
          method: "GET",
          params: queryParams,
        };
      },
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileUrl = (result.data?.data as Record<string, unknown>)?.fileUrl;
          if (typeof fileUrl === "string") {
            window.open(fileUrl, "_blank");
          }
        } catch (error) {
          console.error("Failed to download assignment history:", error);
        }
      },
    }),
    updateAssignmentFine: builder.mutation<
      ApiResponse,
      { assignment_id: string; type: "credit" | "debit"; amount: number }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE}/assignment-fine`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["VehicleAssignmentHistory"],
    }),
    createDriverFine: builder.mutation<
      ApiResponse,
      { user_id: string; type: "credit" | "debit"; amount: number; assignment_id?: string }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE}/driver-fine`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleAssignmentHistory"],
    }),
  }),
});

export type DriverForAssignment = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile_number: string;
};

export type VehicleAssignmentHistoryDriver = {
  name?: string;
  email?: string;
  phone?: string;
  photo?: string;
};

export type VehicleAssignmentHistoryRow = {
  _id?: string;
  vehicle_id?: string;
  vehicle_registration?: string;
  vehicle_name?: string;
  driver_id?: string;
  driver?: VehicleAssignmentHistoryDriver;
  driver_name?: string;
  driver_email?: string;
  start_at?: string;
  end_at?: string;
  status?: string;
  fine?: number;
  notes?: string;
  createdAt?: string;
};

export const {
  useGetVehiclesQuery,
  useGetActiveVehicleListQuery,
  useGetVehicleDetailsQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useUpdateVehicleStatusMutation,
  useDeleteVehicleMutation,
  useBulkUpdateVehicleStatusMutation,
  useBulkDeleteVehiclesMutation,
  useDownloadVehicleReportMutation,
  useRemoveVehicleDriverAssignmentMutation,
  useLazyGetDriversForAssignmentQuery,
  useAssignVehicleScheduleMutation,
  useGetVehicleAssignmentHistoryQuery,
  useDownloadAssignmentHistoryMutation,
  useUpdateAssignmentFineMutation,
  useCreateDriverFineMutation,
} = vehicleApi;
